import { useMemo } from "react";
import { useSelector } from "react-redux";

import { selectAllAccounts, selectGuestAccounts, useGetAccountsQuery, useGetGuestAccountsQuery } from "fond/api";

export interface SwitchAccount {
  ID: string;
  Name: string;
  Guest: boolean;
}

export interface UseSwitchableAccountsResult {
  isLoading: boolean;
  accounts: SwitchAccount[];
}

/**
 * Gets all accounts available for switching, including both user member accounts and guest accounts
 * @returns Object with isLoading state and array of accounts with ID, Name, and Guest flag
 */
export const useSwitchableAccounts = (): UseSwitchableAccountsResult => {
  const { isLoading: isLoadingAccounts } = useGetAccountsQuery();
  const { isLoading: isLoadingGuestAccounts } = useGetGuestAccountsQuery();
  const userAccounts = useSelector(selectAllAccounts);
  const guestAccounts = useSelector(selectGuestAccounts);

  const isLoading = isLoadingAccounts || isLoadingGuestAccounts;

  const accounts = useMemo(() => {
    const accountsMap = new Map<string, SwitchAccount>();
    const userAccountIds = new Set(userAccounts.map((account) => account.ID));

    // Add all user member accounts
    userAccounts.forEach((account) => {
      accountsMap.set(account.ID, {
        ID: account.ID,
        Name: account.Name,
        Guest: false,
      });
    });

    // Add guest accounts from items where user is not a member
    guestAccounts.forEach((account) => {
      if (!userAccountIds.has(account.ID)) {
        accountsMap.set(account.ID, {
          ID: account.ID,
          Name: account.Name,
          Guest: true,
        });
      }
    });

    return Array.from(accountsMap.values());
  }, [userAccounts, guestAccounts]);

  return { isLoading, accounts };
};
